<template>
    <AppLayout>
        <!-- Page Title and Breadcrumb-->
        <Breadcrumb
            :items="breadcrumbItems"
            :home-label="$t('LBL_HOME')"
            :home-url="route('landing')"
            class="block md:hidden px-2"
        />
        <!-- breadcrumb end -->

        <div class="min-h-screen flex flex-col relative pb-28 lg:pb-10">
            <!-- Main container -->
            <div class="container mx-auto px-2 py-6">
                <!-- Main Grid -->
                <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
                    <div class="grid grid-cols-6 gap-6 md:col-span-12 lg:col-span-9">
                        <!-- Product Images -->
                        <div class="col-span-6 md:col-span-3 lg:col-span-3">
                            <!-- Main Image Clickable -->
                            <div class="relative">
                                <img
                                    :src="selectedImage || product.images[0]?.image_path || common.defaultImg"
                                    alt="Product Image"
                                    class="w-full object-contain bg-white rounded-lg shadow"
                                />

                                <!-- Unified Icon + Hover Text Button -->
                                <div class="absolute bottom-2 left-2 group cursor-pointer">
                                    <!-- Icon with Hover Text -->
                                    <div
                                        class="absolute bottom-2 left-2 flex items-center space-x-0 group cursor-pointer"
                                        @click="openImageModal(activeImageIndex)"
                                    >
                                        <!-- Icon -->
                                        <div class="text-white bg-white border rounded-full p-3">
                                            <EnlargeIcon
                                                class="flex-shrink-0 w-6 h-6"
                                                fill="text-gray"
                                            />
                                        </div>

                                        <!-- Text that appears on hover of icon -->
                                        <div
                                            class="text-gray bg-white border p-3 rounded-full transform scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300 whitespace-nowrap"
                                        >
                                            Click to Enlarge
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Thumbnails in Swiper -->
                            <swiper
                                class="mt-3"
                                :options="thumbnailSwiperOptions"
                                ref="thumbnailSwiper"
                            >
                                <swiper-slide
                                    v-for="(img, index) in product.images"
                                    :key="'thumb-' + index"
                                    class="cursor-pointer"
                                >
                                    <img
                                        :src="img.image_path"
                                        @click="selectedImage = img.image_path"
                                        class="w-16 h-16 object-cover rounded-lg border hover:ring-2 ring-green-500"
                                    />
                                </swiper-slide>
                            </swiper>

                            <!-- Image Modal with Slider -->
                            <div
                                v-if="showModal"
                                class="fixed inset-0 z-50 bg-gray-500 bg-opacity-85 flex items-center justify-center"
                            >
                                <div class="relative w-full max-w-4xl px-4">
                                    <!-- Close Button -->
                                    <button
                                        class="absolute top-4 right-4 z-50 text-gray text-4xl font-bold hover:text-gray-300"
                                        @click="closeImageModal"
                                    >
                                        &times;
                                    </button>

                                    <swiper
                                        ref="modalSwiper"
                                        :options="modalSwiperOptions"
                                        class="w-full mb-4"
                                        style="height: calc(100vh - 140px)"
                                    >
                                        <swiper-slide
                                            v-for="(img, index) in product.images"
                                            :key="'modal-slide-' + index"
                                        >
                                            <img
                                                :src="img.image_path"
                                                class="object-contain h-full w-full mx-auto"
                                            />
                                        </swiper-slide>

                                        <!-- Left/Right Arrows (using Swiper slots or custom styling) -->
                                        <div
                                            class="swiper-button-prev text-gray"
                                            slot="button-prev"
                                        ></div>
                                        <div
                                            class="swiper-button-next text-gray"
                                            slot="button-next"
                                        ></div>
                                    </swiper>

                                    <!-- Thumbnails -->
                                    <swiper
                                        ref="modalThumbnailSwiper"
                                        :options="modalThumbnailSwiperOptions"
                                        class="w-full max-w-4xl h-20"
                                    >
                                        <swiper-slide
                                            v-for="(img, index) in product.images"
                                            :key="'thumb-' + index"
                                            class="cursor-pointer"
                                            @click.native="goToSlide(index)"
                                        >
                                            <img
                                                :src="img.image_path"
                                                class="h-20 object-cover border-2 border-transparent hover:border-white mx-auto"
                                            />
                                        </swiper-slide>
                                    </swiper>
                                </div>
                            </div>
                        </div>

                        <!-- Product Info -->
                        <div class="flex flex-col col-span-6 md:col-span-3 lg:col-span-3">
                            <div>
                                <Breadcrumb
                                    :items="breadcrumbItems"
                                    :home-label="$t('LBL_HOME')"
                                    :home-url="route('landing')"
                                    class="hidden md:block"
                                />
                                <p class="text-green-700 font-medium mt-1">{{ product.brand.name }}</p>
                                <h1 class="text-lg lg:text-2xl font-semibold text-gray-800">{{ product.name }}</h1>
                                <div
                                    class="mt-3 text-3xl font-bold text-green-600"
                                    v-if="$page.props.user && kycVerified"
                                >
                                    {{ config(common.currency) }} {{ parseFloat(product.price).toFixed(2) }}
                                </div>
                                <div
                                    v-else
                                    class="mt-3 text-3xl font-bold text-gray-400"
                                >
                                    $$$$
                                </div>
                            </div>

                            <!-- Condition & Stock -->
                            <div class="text-sm text-gray-700 space-y-2 py-2 mt-4">
                                <p><span class="font-semibold">Condition:</span> {{ product.product_status }}</p>
                                <p>
                                    <span class="font-semibold">Available Stock:</span>
                                    {{ isRetailer && kycVerified ? availableStock(product) : '--' }}
                                </p>
                            </div>

                            <!-- Payment Options -->
                            <div class="space-y-4">
                                <p class="text-sm font-semibold text-gray-700 mb-1">Payment Options:</p>
                                <div class="flex flex-wrap items-center gap-2">
                                    <Tooltip
                                        id="tooltip-cod"
                                        text="Cash on Delivery Payment"
                                    >
                                        <CODIcon class="flex-shrink-0 w-6 h-6 mr-2" />
                                    </Tooltip>

                                    <Tooltip
                                        id="tooltip-mastercard"
                                        text="Pay securely with your card (Visa/Master)"
                                    >
                                        <MasterCardIcon class="flex-shrink-0 w-6 h-6 mr-2" />
                                    </Tooltip>
                                    <!-- Add more as needed -->
                                </div>
                            </div>

                            <!-- Desktop Add to Cart -->
                            <div class="mt-8 hidden lg:block">
                                <div class="flex flex-col justify-between items-center space-x-4 space-y-2 md:flex-row">
                                    <!-- Retailer with KYC: Show Quantity Counter -->
                                    <div v-if="product.available_stock > 0 && $page.props.user && kycVerified">
                                        <CounterComponent
                                            class="w-full"
                                            :current-count="quantity"
                                            :max="product.available_stock"
                                            @countChanged="quantity = $event.quantity"
                                        />
                                    </div>
                                    <!-- Admin or Wholesaler: Simple Add to Cart -->
                                    <t-button
                                        v-if="isAdmin || isWholesaler"
                                        class="w-full"
                                        variant="warn"
                                    >
                                        Add to cart
                                    </t-button>

                                    <!-- Authenticated, KYC Verified, Not Admin/Wholesaler -->
                                    <template v-if="$page.props.user && kycVerified && !isAdmin && !isWholesaler">
                                        <t-button
                                            :disabled="isButtonDisabled"
                                            class="w-full"
                                            variant="warn"
                                            @click="addToCart(product, false)"
                                        >
                                            {{ isOutOfStock ? 'Out Of Stock' : 'Add to cart' }}
                                        </t-button>

                                        <t-button
                                            v-if="!isOutOfStock"
                                            :disabled="isButtonDisabled"
                                            class="w-full"
                                            variant="success"
                                            @click="addToCart(product, true)"
                                        >
                                            Buy it Now
                                        </t-button>
                                    </template>

                                    <!-- Not Logged In -->
                                    <t-button
                                        v-else-if="!$page.props.user"
                                        variant="warn"
                                        class="w-full py-2 bg-color-green"
                                        @click="goto('login')"
                                    >
                                        Login to place order.
                                    </t-button>

                                    <!-- Logged In but KYC not verified -->
                                    <t-button
                                        v-else-if="!isAdmin && !kycVerified"
                                        class="w-full p-2"
                                        variant="warn"
                                        @click="
                                            isKycSubmitted
                                                ? goto('kyc.show', { kycId: $page.props.user.kyc_id })
                                                : goto('kyc.create')
                                        "
                                    >
                                        Please Verify Kyc first.
                                    </t-button>
                                </div>
                            </div>

                            <div
                                class="my-6 line-clamp-4"
                                v-html="product.details"
                            />
                        </div>

                        <!-- Collapsible Sections -->
                        <div class="col-span-6 mt-10 space-y-4">
                            <!-- Description -->
                            <Disclosure :defaultOpen="true">
                                <template #title>
                                    <h2 class="text-lg font-semibold text-gray-800 dark:text-white">
                                        Product Description
                                    </h2>
                                </template>
                                <div v-if="!product.details">No Product Detail Available.</div>
                                <div
                                    v-else
                                    v-html="product.details"
                                />
                            </Disclosure>

                            <!-- Shipping and Delivery details -->
                            <Disclosure title="Specifications">
                                <template #title>
                                    <h2 class="text-lg font-semibold text-gray-800 dark:text-white">
                                        Shipping and Delivery
                                    </h2>
                                </template>
                                <div
                                    v-if="!product.shipping_and_delivery"
                                    class="col-span-3"
                                >
                                    No Shipping and Delivery Information Available.
                                </div>
                                <div
                                    v-else
                                    class="col-span-3"
                                    v-html="product.shipping_and_delivery"
                                />

                                <!-- <ul class="list-disc list-inside text-sm text-gray-600 space-y-1">
                                    <li v-if="product.sku">SKU: {{ product.sku }}</li>
                                    <li v-if="product.category">Category: {{ product.category.name }}</li>
                                </ul> -->
                            </Disclosure>

                            <!-- Payments and Returns details -->
                            <Disclosure title="Customer Reviews">
                                <template #title>
                                    <h2 class="text-lg font-semibold text-gray-800 dark:text-white">
                                        Payments and Returns
                                    </h2>
                                </template>
                                <div
                                    v-if="!product.payments_and_returns"
                                    class="col-span-3"
                                >
                                    No Payments and Returns Information Available.
                                </div>
                                <div
                                    v-else
                                    class="col-span-3"
                                    v-html="product.payments_and_returns"
                                />
                            </Disclosure>
                        </div>
                    </div>

                    <!-- Right: Sidebar with Humtt, Seller Info, Brands -->
                    <div class="md:col-span-12 lg:col-span-3 space-y-4">
                        <!-- <div class="flex flex-col justify-between"> -->
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-6">
                            <div class="px-3 py-2 mb-2 border rounded">
                                <div class="font-bold">Shop with confidence</div>
                                <div class="flex mt-2 space-x-2">
                                    <div class="w-16">
                                        <AppLogo logo-only />
                                    </div>
                                    <div>
                                        <div class="font-semibold">Humtt Guarantee</div>
                                        <div class="text-sm text-gray-400">
                                            Get the item you ordered or get your money back
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="px-3 py-2 mb-2 border rounded">
                                <div class="font-bold">Seller Information</div>
                                <div class="flex font-bold text-green-600">
                                    {{ product.owner.name }}
                                </div>
                                <div
                                    class="cursor-pointer"
                                    :title="`Visit store of ${product.owner.name}`"
                                    @click.stop="goto('products.index', { wholesalerIds: [product.owner.id] })"
                                >
                                    <span class="text-sm text-black-600">Visit Store</span>
                                </div>
                                <div
                                    class="cursor-pointer"
                                    :title="`Chat with ${product.owner.name}`"
                                    @click.stop="goto('chat.show', { recipientId: product.owner.id })"
                                >
                                    <!-- <ChatIcon class="w-5 duration-200 transform hover:scale-125" /> -->
                                    <span class="text-sm text-black-600">Contact Seller</span>
                                </div>
                            </div>

                            <!-- brand -->
                            <div class="hidden px-3 py-2 border rounded lg:block">
                                <div class="mb-1 font-bold">Brands</div>
                                <ul>
                                    <li
                                        v-for="brand in brands"
                                        :key="'brand' + brand.id"
                                        class="flex items-center p-3 cursor-pointer hover:bg-gray-100"
                                        @click="
                                            brandIds = [brand.id];
                                            fetchFilteredProducts();
                                        "
                                    >
                                        <img
                                            :src="
                                                brand.images.length
                                                    ? brand.images[0].image_path
                                                    : `https://ui-avatars.com/api/?name=${brand.name}&color=7F9CF5&background=EBF4FF`
                                            "
                                            :alt="brand.name"
                                            class="h-8 mr-4"
                                        />
                                        <p class="line-clamp-1">
                                            {{ brand.name }}
                                        </p>
                                    </li>
                                    <li
                                        v-if="brands.length >= 10"
                                        class="px-3 py-2"
                                        @click="goto('brands.index')"
                                    >
                                        <div
                                            class="inline-block px-2 py-1 text-xs text-white bg-green-500 rounded cursor-pointer hover:bg-green-400"
                                        >
                                            View more
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Mobile Sticky Add to Cart -->
                <div
                    class="fixed bottom-0 left-0 w-full bg-white border-t p-4 lg:hidden shadow-md z-50"
                    v-if="product"
                >
                    <div class="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
                        <!-- Price and Quantity -->
                        <div class="flex-1">
                            <div class="text-base font-semibold text-blue-600">
                                {{ config(common.currency) }} {{ parseFloat(product.price).toFixed(2) }}
                            </div>
                            <div class="text-sm text-gray-500">Qty: {{ quantity }}</div>
                        </div>

                        <!-- Quantity Counter for Retailers with KYC -->
                        <div
                            v-if="isRetailer && kycVerified"
                            class="flex-1 mt-2 sm:mt-0"
                        >
                            <CounterComponent
                                class="w-full"
                                :current-count="quantity"
                                :max="product.available_stock"
                                @countChanged="quantity = $event"
                            />
                        </div>

                        <!-- Admin or Wholesaler Button -->
                        <t-button
                            v-if="isAdmin || isWholesaler"
                            class="w-full sm:w-auto"
                            variant="warn"
                        >
                            Add to Cart
                        </t-button>

                        <!-- KYC Verified Regular Users (Retailer or Salesperson) -->
                        <template v-if="$page.props.user && kycVerified && !isAdmin && !isWholesaler">
                            <t-button
                                class="w-full sm:w-auto"
                                variant="warn"
                                :disabled="isButtonDisabled"
                                @click="addToCart(product, false)"
                            >
                                {{ isOutOfStock ? 'Out Of Stock' : 'Add to Cart' }}
                            </t-button>

                            <!-- <t-button
                                v-if="!isOutOfStock"
                                class="w-full ml-2 sm:w-auto mt-2 sm:mt-0"
                                variant="success"
                                :disabled="isButtonDisabled"
                                @click="addToCart(product, true)"
                            >
                                Buy it Now
                            </t-button> -->
                        </template>

                        <!-- Not Logged In -->
                        <t-button
                            v-else-if="!$page.props.user"
                            class="w-full sm:w-auto mt-2 sm:mt-0 bg-color-green"
                            variant="warn"
                            @click="goto('login')"
                        >
                            Login to place order.
                        </t-button>

                        <!-- Logged In but KYC not Verified -->
                        <t-button
                            v-else-if="!isAdmin && !kycVerified"
                            class="w-full sm:w-auto mt-2 sm:mt-0"
                            variant="warn"
                            @click="
                                isKycSubmitted
                                    ? goto('kyc.show', { kycId: $page.props.user.kyc_id })
                                    : goto('kyc.create')
                            "
                        >
                            Please Verify KYC First
                        </t-button>
                    </div>
                </div>

                <!-- popular brands -->
                <div
                    v-if="popularBrands.length"
                    class="my-16"
                >
                    <div class="mb-4 text-xl font-bold">Popular Brands</div>
                    <div class="flex flex-wrap items-center gap-4 md:gap-8">
                        <img
                            v-for="popularBrand in popularBrands"
                            :key="'popular-brand-' + popularBrand.id"
                            :src="
                                popularBrand.images.length
                                    ? popularBrand.images[0].image_path
                                    : `https://ui-avatars.com/api/?name=${popularBrand.name}&color=7F9CF5&background=EBF4FF`
                            "
                            class="p-1 rounded-full cursor-pointer h-14 w-14 md:w-28 md:h-28 ring"
                            @click="goto('products.index', { brandIds: [popularBrand.id] })"
                        />
                    </div>
                </div>

                <!-- popular products -->
                <div
                    v-if="popularProducts.length"
                    class="my-16"
                >
                    <div class="mb-4 text-xl font-bold">Popular Products</div>
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-6">
                        <div
                            v-for="popularProduct in popularProducts"
                            :key="'popular-product-' + popularProduct.id"
                            class="w-full overflow-hidden transition-transform ease-in-out transform bg-white rounded cursor-pointer h-60 hover:-translate-y-2 hover:shadow-lg"
                            @click="
                                goto('products.show', {
                                    dummy: `${convertToKebabCase(popularProduct.brand.name)}-${convertToKebabCase(
                                        popularProduct.name,
                                    )}`,
                                    product: popularProduct.slug,
                                })
                            "
                        >
                            <img
                                :src="
                                    popularProduct.images.length
                                        ? popularProduct.images[0].image_path
                                        : common.defaultImg
                                "
                                :alt="popularProduct.name"
                                class="w-full h-3/5"
                            />
                            <div class="p-2">
                                <p class="text-base text-green-800 line-clamp-3 hover:underline hover:text-yellow-600">
                                    {{ popularProduct.name }}
                                </p>
                                <div
                                    v-if="$page.props.user && kycVerified"
                                    class="text-lg font-semibold"
                                >
                                    {{ config(common.currency, product.owner) }}
                                    {{ parseFloat(popularProduct.price).toFixed(2) }}
                                </div>
                                <div
                                    v-else
                                    class="text-lg"
                                >
                                    $$$$
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Recently Added -->
                <section
                    v-if="recentlyAddedProducts.length"
                    class="py-8 bg-white"
                >
                    <div class="mx-auto px-0">
                        <!-- sub categories -->
                        <h2 class="text-xl font-bold md:text-2xl mb-10">Recently Added</h2>
                        <swiper
                            ref="swiperRecentProductRef"
                            :options="recentProductsSwiperOptions"
                            class="pb-6"
                            @ready="onSwiperReady('swiperRecentProductRef', $event)"
                            @mouseenter.native="pauseAutoplay('swiperRecentProductRef')"
                            @mouseleave.native="resumeAutoplay('swiperRecentProductRef')"
                        >
                            <swiper-slide
                                v-for="(product, index) in recentlyAddedProducts"
                                :key="'new-products-' + index"
                            >
                                <ProductCard :product="product" />
                            </swiper-slide>
                        </swiper>
                    </div>
                </section>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
    import 'swiper/css/swiper.css';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import CounterComponent from '@/Components/CounterComponent.vue';
    import BreadCrumbs from '@/Components/BreadCrumbs.vue';
    import Breadcrumb from '../../Components/Breadcrumb.vue';
    import AppLogo from '@/Components/AppLogo.vue';
    import common from '@/common';
    import ProductCard from '@/Components/ProductCard.vue';
    import Disclosure from '@/Components/Disclosure.vue';
    import CODIcon from '@/Components/Icons/CODIcon';
    import MasterCardIcon from '@/Components/Icons/MasterCardIcon';
    import EnlargeIcon from '../../Components/Icons/EnlargeIcon.vue';
    import Tooltip from '@/Components/Tooltip.vue';
    import { fill } from 'lodash';

    export default {
        name: 'ProductsShow',

        metaInfo() {
            return {
                title: this.product.name,
                titleTemplate: 'Hummtt - %s',
                meta: [
                    {
                        vmid: 'description',
                        name: 'description',
                        content: `Page with details about product - ${this.product.name}`,
                    },
                    {
                        vmid: 'keywords',
                        name: 'keywords',
                        content: `${this.product.name}, ${this.product.brand.name}, ${this.product.category.name}`,
                    },
                ],
            };
        },

        components: {
            AppLayout,
            Swiper,
            SwiperSlide,
            CounterComponent,
            Disclosure,
            BreadCrumbs,
            Breadcrumb,
            AppLogo,
            ProductCard,
            CODIcon,
            MasterCardIcon,
            EnlargeIcon,
            Tooltip,
        },

        props: {
            product: {
                type: Object,
                required: true,
            },
            brands: {
                type: Array,
                required: true,
            },
            popularProducts: {
                type: Array,
                default: () => [],
            },
            recentlyAddedProducts: {
                type: Array,
                default: () => [],
            },
            popularBrands: {
                type: Array,
                default: () => [],
            },
        },

        data() {
            return {
                common,
                quantity: 1,
                showModal: false,
                activeImageIndex: 0,
                selectedImage: '',
                activeModalIndex: 0,
                modalSwiperOptions: {
                    slidesPerView: 1,
                    loop: true,
                    navigation: {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    },
                },
                modalThumbnailSwiperOptions: {
                    spaceBetween: 10,
                    slidesPerView: 6,
                    freeMode: true,
                    watchSlidesVisibility: true,
                    watchSlidesProgress: true,
                },

                breadCrumbs: [{ title: 'Products', link: 'products.index' }, { title: this.product.name }],
                breadcrumbItems: [{ label: 'Products', href: 'products.index' }, { label: this.product.name }],
                brandIds: [],
            };
        },

        watch: {
            showModal(val) {
                if (val) {
                    document.body.style.overflow = 'hidden';
                } else {
                    document.body.style.overflow = '';
                }
            },
        },

        mounted() {
            this.$nextTick(() => {
                console.log('Mounted modalSwiper:', this.$refs.modalSwiper?.swiper);
                console.log('Mounted thumbnailSwiper:', this.$refs.modalThumbnailSwiper?.swiper);
            });
        },

        computed: {
            isOutOfStock() {
                return parseInt(this.product.available_stock) === 0;
            },

            isButtonDisabled() {
                const stock = parseInt(this.product.available_stock);
                return (
                    !this.quantity ||
                    stock === 0 ||
                    parseInt(this.quantity) > stock ||
                    (this.isSalesperson &&
                        this.$page.props.user.inverse_associated_users[0]?.id !== this.product.owner.id)
                );
            },

            recentProductsSwiperOptions() {
                return {
                    ...this.defaultSwiperOptions,
                    spaceBetween: 20,
                    autoplay: {
                        ...this.defaultSwiperOptions.autoplay,
                        delay: 2000,
                    },
                };
            },

            thumbnailSwiperOptions() {
                return {
                    ...this.defaultSwiperOptions,
                    slidesPerView: 4, // override
                    spaceBetween: 10, // override
                    loop: false, // override if needed
                    autoplay: false, // disable autoplay for thumbnails
                    freeMode: true, // allow manual scrolling
                    watchSlidesVisibility: true,
                    breakpoints: {
                        320: { slidesPerView: 4 },
                        425: { slidesPerView: 5 },
                        640: { slidesPerView: 6 },
                        768: { slidesPerView: 5 },
                        1024: { slidesPerView: 5 },
                        1440: { slidesPerView: 6 },
                    },
                };
            },
        },

        methods: {
            fetchFilteredProducts() {
                this.$inertia.get(route('products.index', { brandIds: this.brandIds }));
            },

            addToCart(product, isToCart) {
                if (
                    (this.isRetailer ||
                        (this.isSalesperson &&
                            this.$page.props.user.inverse_associated_users[0].id === product.owner.id)) &&
                    parseInt(product.available_stock)
                ) {
                    this.$inertia.post(
                        route('cartItems.store'),
                        { product_id: product.id, quantity: this.quantity, isToCart },
                        { preserveScroll: true },
                    );
                }
            },

            availableStock(product) {
                if (product.available_stock === 0) {
                    return 'Out Of Stock.';
                }

                return product.available_stock < 10000 ? product.available_stock : '10K+';
            },

            openImageModal(index) {
                this.activeImageIndex = index;
                this.showModal = true;
                this.$nextTick(() => {
                    const swiper = this.$refs.modalSwiper.swiper;
                    if (swiper) swiper.slideTo(index, 0);
                });
            },

            closeImageModal() {
                this.showModal = false;
            },

            goToSlide(index) {
                console.log('Clicked thumbnail index:', index);
                const mainSwiper = this.$refs.modalSwiper?.swiper;
                if (mainSwiper) {
                    mainSwiper.slideTo(index);
                } else {
                    console.warn('mainSwiper is undefined');
                }
            },
        },
    };
</script>
